import { Room, RoomEvent, Track } from 'livekit-client';
import { useEffect } from 'react';

const FRAME_INTERVAL = 1000 / 30;

function createRotationProcessor(angleDeg: 90 | -90) {
  let canvas: HTMLCanvasElement | null = null;
  let ctx: CanvasRenderingContext2D | null = null;
  let sourceVideo: HTMLVideoElement | null = null;
  let rafId = 0;
  const rad = (angleDeg * Math.PI) / 180;

  async function setup(track: MediaStreamTrack) {
    sourceVideo = document.createElement('video');
    sourceVideo.srcObject = new MediaStream([track]);
    sourceVideo.muted = true;
    sourceVideo.playsInline = true;
    await sourceVideo.play();

    if (!sourceVideo.videoWidth) {
      await new Promise<void>((resolve) => {
        sourceVideo!.addEventListener('loadedmetadata', () => resolve(), { once: true });
      });
    }

    const iw = sourceVideo.videoWidth;
    const ih = sourceVideo.videoHeight;

    // Output canvas has swapped dimensions (portrait → landscape or vice-versa)
    canvas = document.createElement('canvas');
    canvas.width = ih;
    canvas.height = iw;
    ctx = canvas.getContext('2d')!;

    let lastTime = 0;
    const render = (now: number) => {
      rafId = requestAnimationFrame(render);
      if (now - lastTime < FRAME_INTERVAL) return;
      lastTime = now;
      if (!ctx || !sourceVideo || !canvas) return;
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rad);
      ctx.drawImage(sourceVideo, -iw / 2, -ih / 2, iw, ih);
      ctx.restore();
    };
    rafId = requestAnimationFrame(render);
  }

  return {
    name: 'rotation-fix',
    processedTrack: undefined as MediaStreamTrack | undefined,

    async init({ track }: { kind: unknown; track: MediaStreamTrack }) {
      await setup(track);
      this.processedTrack = canvas!.captureStream(30).getVideoTracks()[0];
    },

    async restart({ track }: { kind: unknown; track: MediaStreamTrack }) {
      cancelAnimationFrame(rafId);
      if (sourceVideo) {
        sourceVideo.srcObject = null;
        sourceVideo = null;
      }
      canvas = null;
      ctx = null;
      await setup(track);
      this.processedTrack = canvas!.captureStream(30).getVideoTracks()[0];
    },

    async destroy() {
      cancelAnimationFrame(rafId);
      if (sourceVideo) {
        sourceVideo.srcObject = null;
        sourceVideo = null;
      }
      this.processedTrack?.stop();
      this.processedTrack = undefined;
      canvas = null;
      ctx = null;
    },
  };
}

/**
 * Canvas rotation needed to correct Gecko's unrotated camera output.
 *
 * Gecko's portrait stream has the camera "top" pointing up.
 * When the phone is rotated:
 *   angle=90  (landscape-left, top→left)  → rotate CCW (-90°) to upright the content
 *   angle=270 (landscape-right, top→right) → rotate CW  (+90°) to upright the content
 *   angle=0/180 (portrait)                → no rotation needed
 */
function geckoRotation(screenAngle: number): 90 | -90 | 0 {
  if (screenAngle === 90 || screenAngle === -90) return 90;
  if (screenAngle === 270) return -90;
  return 0;
}

function currentAngle(): number {
  return screen.orientation?.angle ?? (window.orientation as number) ?? 0;
}

function isLandscape(angle: number): boolean {
  return angle === 90 || angle === 270 || angle === -90;
}

/**
 * Detects Gecko (Firefox / Fennec) by UA string.
 * Gecko doesn't apply the camera's rotation metadata when sending WebRTC streams,
 * so we need a canvas processor. Other browsers handle it natively via restartTrack.
 */
function isGecko(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    /Gecko\/\d/.test(navigator.userAgent) &&
    !/like Gecko/.test(navigator.userAgent)
  );
}

export function useCameraOrientation(room: Room) {
  useEffect(() => {
    const gecko = isGecko();

    const apply = async () => {
      const angle = currentAngle();
      const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const track = pub?.videoTrack;
      if (!track) return;

      if (gecko) {
        // On Gecko: do NOT call restartTrack — doing so triggers the metadata bug.
        // Instead, rotate the native portrait stream via canvas as needed.
        const rot = geckoRotation(angle);
        if (rot === 0) {
          track.stopProcessor();
        } else {
          await track.setProcessor(createRotationProcessor(rot) as any);
        }
      } else {
        // Chrome / Safari / other: restartTrack with correct dimensions;
        // the browser applies rotation metadata natively.
        await track.restartTrack({
          resolution: isLandscape(angle)
            ? { width: 1280, height: 720, frameRate: 30 }
            : { width: 720, height: 1280, frameRate: 30 },
        });
      }
    };

    // Track published after mount (normal join flow)
    const onTrackPublished = () => {
      apply();
    };

    screen.orientation?.addEventListener('change', apply);
    window.addEventListener('orientationchange', apply); // legacy iOS
    room.on(RoomEvent.LocalTrackPublished, onTrackPublished);

    // Handle joining while already in landscape
    apply();

    return () => {
      screen.orientation?.removeEventListener('change', apply);
      window.removeEventListener('orientationchange', apply);
      room.off(RoomEvent.LocalTrackPublished, onTrackPublished);
      if (gecko) {
        const pub = room.localParticipant.getTrackPublication(Track.Source.Camera);
        pub?.videoTrack?.stopProcessor();
      }
    };
  }, [room]);
}
